import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Mount-fetch hook that encapsulates the cleanup-flag boilerplate
 * (`let cancelled = false; ...; return () => { cancelled = true; }`)
 * which was previously duplicated in 9 mentorship components.
 *
 * Contract:
 *   const { data, loading, error, refetch } = useFetchOnMount(fetcher, deps);
 *
 * - `fetcher` is invoked once on mount and again whenever `deps` change.
 * - The hook tracks a per-effect "alive" ref so a stale request that
 *   resolves after unmount cannot setState on a dead component.
 * - `refetch()` re-runs the fetcher imperatively (e.g. after a mutation).
 *   The latest call always wins, prior in-flight responses are ignored.
 * - `error` carries the thrown value (typed as unknown for honesty).
 *
 * Convention here mirrors the existing service layer: fetchers `throw`
 * on failure, the hook converts that to an `error` slot. No retry, no
 * cache — components that need a refresh-after-mutate flow call
 * `refetch()` from their event handler.
 */
export type FetchState<T> = {
    data: T | null;
    loading: boolean;
    error: unknown;
    refetch: () => Promise<void>;
};

export function useFetchOnMount<T>(
    fetcher: () => Promise<T>,
    deps: ReadonlyArray<unknown> = [],
): FetchState<T> {
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<unknown>(null);
    // Bumped on each invocation so an older in-flight request that resolves
    // after a newer one is harmlessly discarded.
    const requestIdRef = useRef(0);

    const run = useCallback(async () => {
        const myRequestId = ++requestIdRef.current;
        setLoading(true);
        setError(null);
        try {
            const result = await fetcher();
            if (requestIdRef.current !== myRequestId) return;
            setData(result);
        } catch (err) {
            if (requestIdRef.current !== myRequestId) return;
            setError(err);
            console.error('[useFetchOnMount] fetch failed', err);
        } finally {
            if (requestIdRef.current === myRequestId) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    useEffect(() => {
        void run();
        // On unmount or deps-change, bumping requestIdRef in the next `run`
        // is what cancels in-flight writes — no boolean flag needed.
    }, [run]);

    return { data, loading, error, refetch: run };
}
